<template>
  <main class="main main--has-floating-buttons">
    <card :title="$t('pageRoomplan.card.roomPlan')" cols="12">
      <hm-card-inner id="date-selection">
        <hm-col :grid="true" cols="12" class="z-index40">
          <hm-input-date v-model="startDate" :label="$t('pageRoomplan.startDate')" cols="12 2@tablet 2@desktop 1@hd" name="startDate" />
          <hm-input-select v-model="numberOfDays"
                           :help="`${numberOfDays > 30 ? 'Mehr als 40 sichtbare Tage können die Geschwindigkeit negativ beeinflussen.' : ''}`"
                           :has-warning="numberOfDays > 30"
                           :label="$t('pageRoomplan.numberOfDays')"
                           name="numberOfDays"
                           cols="12 2@tablet 2@desktop 1@hd">
            <option :value="7">7</option>
            <option :value="14">14</option>
            <option :value="21">21</option>
            <option :value="30">30</option>
            <option :value="40">40</option>
            <option :value="80">80</option>
            <option :value="120">120</option>
          </hm-input-select>
          <hm-input-toggle v-if="anyTouch"
                           v-model="editModeActive"
                           :label="$t('pageRoomplan.editModeActive')"
                           :enabled-title="$t('pageRoomplan.editModeActiveEnabled')"
                           :disabled-title="$t('pageRoomplan.editModeActiveDisbaled')"
                           cols="12 3@tablet 2@desktop 2@hd" />
          <hm-col cols="12 8@tablet 8@desktop 6@hd start-5@tablet start-5@desktop start-7@hd" class="hidden@mobile">
            <div class="button-row">
              <hm-button class="" cols="1" icon-left="back-3" @click="updateDate(-365)">1 Y</hm-button>
              <hm-button class="" cols="1" icon-left="back-2" @click="updateDate(-21)">21 D</hm-button>
              <hm-button class="" cols="1" icon-left="back-1" @click="updateDate(-7)">7 D</hm-button>
              <hm-button class="hidden visible-flex@desktop visible-flex@hd" cols="1" icon-left="back-1" @click="updateDate(-1)">1 D</hm-button>
              <hm-button class="btn--today" cols="1" @click="today()">{{ $t('pageRoomplan.today') }}</hm-button>
              <hm-button class="hidden visible-flex@desktop visible-flex@hd" cols="1" icon-right="forward-1" @click="updateDate(1)">1 D</hm-button>
              <hm-button class="" cols="1" icon-right="forward-1" @click="updateDate(7)">7 D</hm-button>
              <hm-button class="" cols="1" icon-right="forward-2" @click="updateDate(21)">21 D</hm-button>
              <hm-button class="" cols="1" icon-right="forward-3" @click="updateDate(365)">1 Y</hm-button>
            </div>
          </hm-col>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner v-hotkey="hotkeyBinding" :full-size="true">
        <hm-col cols="12">
          <spinner-overlay v-if="reservationUpdateInProgress" />
          <roomplan ref="roomplan"
                    :holidays="holidayMap"
                    :start-date="visibleStartDate"
                    :days="visibleDays"
                    :room-type-availability="roomTypeAvailability"
                    :virtual-room-count="virtualRoomCount"
                    :line-height="lineHeight"
                    :first-column-width="leftColumnWidth"
                    :additional-column-width="columnWidth"
                    :can-assign-room-number="canAssignRoomNumber"
                    :edit-mode-active="editModeActive && !hasRole('ROLE_UNIT_OWNER')"
                    :selectable-rooms="selectableRooms"
                    :selected-rooms="selectedRooms"
                    :show-room-type-plan="showRoomTypePlan && !hasRole('ROLE_UNIT_OWNER')"
                    :inventory="inventory"
                    :housekeeping="housekeepingStatus"
                    @update="updateAvailability()"
                    @addDraftReservation="addDraftReservation"
                    @editFinished="reservationEditFinished"
                    @roomNumberSelected="roomNumberSelected">
            <roomplan-reservation v-for="(reservation, idx) in reservations"
                                  :id="`reservation-${idx}`"
                                  :key="`${reservation.reservationId}::${reservation.roomNumber}::${reservation.checkInDate}::${reservation.checkOutDate}`"
                                  v-model="reservations[idx]"
                                  :unskew="unskew"
                                  :payment-status="paymentStatus[reservation.reservationId]"
                                  @updated="updateReservation"
                                  @hoverroomstart="$refs.roomplan.$emit('hoverroomstart', $event)"
                                  @hoverroomend="$refs.roomplan.$emit('hoverroomend', $event)" />
            <roomplan-reservation v-for="reservation in draftReservations"
                                  :key="reservation.reservationId"
                                  :value="reservation"
                                  :unskew="unskew"
                                  :draft="true"
                                  :ref="reservation.reservationId"
                                  @dblclick="onClickCreateReservation"
                                  @contextmenu="removeDraftReservation(reservation.reservationId)"
                                  @hoverroomstart="$refs.roomplan.$emit('hoverroomstart', $event)"
                                  @hoverroomend="$refs.roomplan.$emit('hoverroomend', $event)" />
          </roomplan>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner id="roomplan-settings" :title="$t('pageRoomplan.displayOptions')" icon="gear">
        <hm-input-select v-model="lineHeight" :label="$t('pageRoomplan.roomPlanLineHeight')" name="roomPlanLineHeight" cols="12 4@tablet 2@desktop 1@hd">
          <option :value="2.5">{{ $t('pageRoomplan.lineHeightOptions.small') }}</option>
          <option :value="3.0">{{ $t('pageRoomplan.lineHeightOptions.medium') }}</option>
          <option :value="3.5">{{ $t('pageRoomplan.lineHeightOptions.large') }}</option>
        </hm-input-select>
        <hm-input-select v-model="leftColumnWidth" :label="$t('pageRoomplan.leftColumnWidth')" name="leftColumnWidth" cols="12 4@tablet 2@desktop 1@hd">
          <option :value="11">{{ $t('pageRoomplan.leftColumnWidthOptions.narrow') }}</option>
          <option :value="16">{{ $t('pageRoomplan.leftColumnWidthOptions.regular') }}</option>
          <option :value="21">{{ $t('pageRoomplan.leftColumnWidthOptions.wide') }}</option>
        </hm-input-select>
        <hm-input-select v-model="columnWidth" :label="$t('pageRoomplan.columnWidth')" name="columnWidth" cols="12 4@tablet 2@desktop 1@hd">
          <option :value="-1.25">{{ $t('pageRoomplan.columnWidthOptions.xsmall') }}</option>
          <option :value="0">{{ $t('pageRoomplan.columnWidthOptions.small') }}</option>
          <option :value="2">{{ $t('pageRoomplan.columnWidthOptions.regular') }}</option>
          <option :value="4">{{ $t('pageRoomplan.columnWidthOptions.medium') }}</option>
          <option :value="6">{{ $t('pageRoomplan.columnWidthOptions.large') }}</option>
          <option :value="8">{{ $t('pageRoomplan.columnWidthOptions.xlarge') }}</option>
        </hm-input-select>
        <hm-input-select v-model="unskew" :label="$t('pageRoomplan.unskew')" name="unskew" cols="12 4@tablet 2@desktop 1@hd">
          <option :value="false">{{ $t('pageRoomplan.skewOptions.skewed') }}</option>
          <option :value="true">{{ $t('pageRoomplan.skewOptions.unskewed') }}</option>
        </hm-input-select>
        <hm-input-toggle v-if="!hasRole('ROLE_UNIT_OWNER')"
                         v-model="showRoomTypePlan"
                         :label="$t('pageRoomplan.showRoomTypePlan')"
                         name="showRoomTypePlan"
                         cols="12 4@tablet 2@desktop 1@hd" />
      </hm-card-inner>
    </card>

    <hm-floating-buttons>
      <!--      <hm-input-toggle v-if="anyTouch" v-model="editModeActive" :label="$t('pageRoomplan.editModeActive')" cols="12 3@tablet 2@desktop 2@hd" />-->
      <hm-floating-button v-if="anyTouch && hasAnyRole(['ROLE_RESERVATION_REQUEST','ROLE_RESERVATION_NOTIFICATION','ROLE_ADMIN','ROLE_SYSADMIN'])"
                          :label="editModeActive ? $t('pageRoomplan.actions.disableEditMode') : $t('pageRoomplan.actions.enableEditMode')"
                          :icon="editModeActive ? 'fill-fingerprint' : 'fill-arrows'"
                          color="gray"
                          @click="editModeActive = !editModeActive" />
      <hm-floating-button v-if="!isEmpty(draftReservations)" :label="$t('pageRoomplan.actions.more')" icon="fill-more" color="gray">
        <template v-for="it in enums.InventoryCountType">
          <hm-floating-child-button :key="it"
                                    :label="$t(`InventoryCountType.${it}`)"
                                    :icon="inventoryIconClass(it)"
                                    color="orange"
                                    @click="setInventoryStatus(it)" />
        </template>
        <hm-floating-child-button :label="$t(`pageRoomplan.actions.removeInventoryStatus`)"
                                  icon="save"
                                  color="mint"
                                  @click="setInventoryStatus(null)" />
      </hm-floating-button>
      <hm-floating-button v-if="!isEmpty(draftReservations)"
                          :label="$t('pageRoomplan.actions.cancelEdit')"
                          icon="delete"
                          color="gray"
                          @click="cancelEdit()" />
      <hm-floating-button :label="$t('pageRoomplan.actions.createReservation')"
                          icon="plus"
                          color="blue"
                          @click="onClickCreateReservation()" />
    </hm-floating-buttons>

  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { isArray, join, isNil, isEmpty, groupBy, values, head, cloneDeep, keyBy, debounce } from 'lodash';
import HmCardInnerLine from '../lib/layout/HmCardInnerLine';
import HmCardInner from '../lib/layout/HmCardInner';
import uuid from 'uuid';
import PageMixin from '../../mixins/PageMixin';
import TimerMixin from '../../mixins/TimerMixin';

export default {
  name: 'PageRoomplan',
  components: { HmCardInner, HmCardInnerLine },
  mixins: [HotelmeisterMixin, PageMixin, TimerMixin],
  props: ['test'],

  data() {
    return {
      debouncedUpdateRoomplan: null,
      hotkeyBinding: { esc: this.cancelEdit },
      reservations: [],
      draftReservations: [],
      roomTypeAvailability: null,
      startDate: null,
      numberOfDays: 30,
      visibleDays: 30,
      visibleStartDate: null,
      lineHeight: 3,
      leftColumnWidth: 11,
      virtualRoomCount: 0,
      columnWidth: 2,
      unskew: false,
      showRoomTypePlan: true,
      editModeActive: !this.$store.getters['layout/touchOnly'],
      selectableRooms: [],
      selectedRooms: {},
      reservationUpdateInProgress: false,
      paymentStatus: {},
      housekeepingStatus: {},
      inventory: [],
      lastUpdate: null,
      holidays: null,
    };
  },

  computed: {
    holidayMap: function () {
      return groupBy(this.holidays || [], it => this.$moment(it.date, 'YYYYMMDD').format('YYYY-MM-DD'));
    },
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    rooms: function () {
      return isNil(this.hotelSettings.rooms) ? [] : this.hotelSettings.rooms.all();
    },
    roomNumbers: function () {
      return isNil(this.hotelSettings.rooms) ? [] : this.hotelSettings.rooms.all().flatMap(room => room.roomNumber);
    },
    touchOnly: function () {
      return this.$store.getters['layout/touchOnly'];
    },
    anyTouch: function () {
      return this.$store.getters['layout/anyTouch'];
    },
    pageShortcuts: function () {
      return {
        'Ctrl+←': this.$t('pageRoomplan.shortcuts.moveBack'),
        'Ctrl+→': this.$t('pageRoomplan.shortcuts.moveForward'),
        'Ctrl+↓': this.$t('pageRoomplan.shortcuts.move1yBack'),
        'Ctrl+↑': this.$t('pageRoomplan.shortcuts.move1yForward'),
        'Ctrl+Pos1': this.$t('pageRoomplan.shortcuts.gotoToday'),
      };
    },
  },

  watch: {
    numberOfDays: function () {
      this.debouncedUpdateRoomplan();
      this.setUserConfig('numberOfDays', this.numberOfDays);
    },
    startDate: function () {
      this.debouncedUpdateRoomplan();
      this.setUserConfig('startDate', this.startDate);
    },
    lineHeight: function () {
      this.setUserConfig('lineHeight', this.lineHeight);
    },
    columnWidth: function () {
      this.setUserConfig('columnWidth', this.columnWidth);
      if (this.columnWidth < 0) {
        this.unskew = true;
      }
    },
    unskew: function () {
      this.setUserConfig('unskew', this.unskew);
      if (this.unskew === false && this.columnWidth < 0) {
        this.columnWidth = 0;
      }
    },
    showRoomTypePlan: function () {
      this.setUserConfig('showRoomTypePlan', this.showRoomTypePlan);
    },
    leftColumnWidth: function () {
      this.setUserConfig('leftColumnWidth', this.leftColumnWidth);
    },
    editModeActive: function (newVal, oldVal) {
      if (newVal !== oldVal && newVal === false) {
        this.cancelEdit();
      }
    },
  },

  created: function () {
    if (!this.hasAnyRole(['ROLE_RESERVATION_REQUEST', 'ROLE_RESERVATION_NOTIFICATION', 'ROLE_ADMIN', 'ROLE_SYSADMIN'])) {
      this.editModeActive = false;
    }

    this.debouncedUpdateRoomplan = debounce(this.updateRoomplan, 250);

    this.numberOfDays = Number(this.getUserConfig('numberOfDays')) || 30;
    this.startDate = this.getUserConfig('startDate') || this.$moment().add(-2, 'days').format('YYYYMMDD');
    this.visibleStartDate = this.startDate;
    this.lineHeight = Number(this.getUserConfig('lineHeight')) || 3;
    this.columnWidth = Number(this.getUserConfig('columnWidth')) || 2;
    this.unskew = this.getUserConfig('unskew') === 'true' || false;
    this.leftColumnWidth = Number(this.getUserConfig('leftColumnWidth')) || 11;
    this.showRoomTypePlan = this.getUserConfig('showRoomTypePlan') === 'true' || isNil(this.getUserConfig('showRoomTypePlan')) || this.getUserConfig('showRoomTypePlan') === true;

    this.debouncedUpdateRoomplan();

    this.$serviceBus.$on('help.show', this.showHelp);

    this.$serviceBus.$on('shortcut.pageRight', this.pageRight);
    this.$serviceBus.$on('shortcut.pageLeft', this.pageLeft);
    this.$serviceBus.$on('shortcut.pageUp', this.pageRightYear);
    this.$serviceBus.$on('shortcut.pageDown', this.pageLeftYear);
    this.$serviceBus.$on('shortcut.scrollTop', this.today);

    const $vm = this;
    this.setInterval(() => {
      if (!isNil($vm.lastUpdate)) {
        const timeSinceLastUpdate = ((new Date().getTime()) - $vm.lastUpdate);
        if (timeSinceLastUpdate > (15 * 60 * 1000)) { // update if it hasn't been updated for more than 15 minutes
          this.updateRoomplan();
        }
      }
    }, 5 * 60 * 1000); // check every 5 minutes if they room plan needs to be updated

    this.loadHolidays();
  },

  beforeDestroy: function () {
    this.$serviceBus.$off('shortcut.pageRight', this.pageRight);
    this.$serviceBus.$off('shortcut.pageLeft', this.pageLeft);
    this.$serviceBus.$off('shortcut.scrollTop', this.today);
    this.$serviceBus.$off('shortcut.pageUp', this.pageRightYear);
    this.$serviceBus.$off('shortcut.pageDown', this.pageLeftYear);
  },

  methods: {
    inventoryIconClass: function (type) {
      if (type === 'OutOfOrder') return 'ban';
      if (type === 'OutOfService') return 'hammer';
      if (type === 'UnderConstruction') return 'digging';
      if (type === 'UnderRenovation') return 'digging';
      if (type === 'OffMarket') return 'ban';
      if (type === 'ContractSold') return 'euro-sign';
      return 'ban';
    },
    updateReservation(reservation) {
      // this.reservations.splice(this.reservations.findIndex((it) => it.reservationId === reservation.reservationId), 1);
    },
    pageRight() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(this.numberOfDays, 'days').format('YYYYMMDD');
    },
    pageLeft() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(-this.numberOfDays, 'days').format('YYYYMMDD');
    },
    pageRightYear() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(1, 'year').format('YYYYMMDD');
    },
    pageLeftYear() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(-1, 'year').format('YYYYMMDD');
    },
    showHelp() {
      this.$driver.defineSteps([
        {
          element: '#date-selection',
          padding: 15,
          popover: {
            title: 'Datum',
            description: 'Um den Zimmerplan für einen bestimmten Zeitraum zu öffnen können Sie entweder ein Datum eingeben oder rechts mit den Tasten vor oder zurück springen.<br/><br/>Im Feld Datum können Sie z.B. auch 30 eingeben um das Datum in 30 Tagen (von heute aus gerechnet) zu öffnen.',
            position: 'bottom-center',
          },
        },
        {
          element: '#reservation-0',
          padding: 0,
          popover: {
            title: 'Reservierung',
            description: 'Die Reservierungen können per Drag-and-Drop in ein anderes Zimmer verschoben werden.',
            position: 'top-center',
          },
        },
        {
          element: '#roomplan-settings',
          padding: 15,
          popover: {
            title: 'Anzeigeoptionen',
            description: 'Hier können Sie die Darstellung an Ihre persönlichen Vorlieben anpasse. Die Einstellungen werden für diesen Benutzer auf diesem Gerät gespeichert.',
            position: 'top-center',
          },
        },
      ]);
      this.$driver.start();
    },
    roomNumberSelected: function (roomNumber, newValue) {
      this.selectedRooms[roomNumber] = newValue;

      if (newValue && isNil(this.draftReservations.find(it => it.roomNumber === roomNumber))) {
        const newDraftRes = cloneDeep(head(this.draftReservations));
        if (isNil(newDraftRes)) return;
        newDraftRes.reservationId = uuid.v4();
        newDraftRes.roomNumber = roomNumber;
        newDraftRes.numAdults = this.findHotelSettingById('rooms', roomNumber, 'roomNumber').roomType.expectedOccupancy;
        this.addDraftReservation(newDraftRes);
      } else if (!newValue && !isNil(this.draftReservations.find(it => it.roomNumber === roomNumber))) {
        this.removeDraftReservation(this.draftReservations.find(it => it.roomNumber === roomNumber).reservationId);
      }
    },
    onClickCreateReservation: function () {
      if (isEmpty(this.draftReservations)) {
        this.$router.push(
          {
            name: 'createReservation',
            params: {
              hotelId: this.$route.params.hotelId,
            },
          },
        );
      } else {
        this.$router.push(
          {
            name: 'createReservation',
            params: {
              reservations: this.draftReservations,
              hotelId: this.$route.params.hotelId,
            },
          },
        );
      }
    },
    cancelEdit: function () {
      this.$refs.roomplan.cancelEdit();
      this.draftReservations = [];
      this.selectableRooms = [];
      this.selectedRooms = {};
    },
    reservationEditFinished: function (reservationId) {
      const draftRes = this.draftReservations.find(it => it.reservationId === reservationId);
      if (isNil(draftRes)) return;
      draftRes.status = 'DraftCreated';
      draftRes.reservationId = uuid.v4();
      // eslint-disable-next-line radix
      if (parseInt(draftRes.checkOutDate) <= parseInt(draftRes.checkInDate)) {
        draftRes.checkOutDate = this.$moment(this.checkInDate, 'YYYYMMDD').add(1, 'days').format('YYYYMMDD');
      }

      this.overlapsWithOtherReservations(draftRes, true);

      this.updateSelectableRooms(draftRes);
    },
    overlapsWithOtherReservations: function (draftRes, displayMessage = false) {
      if (this.overlapsWithSuppliedReservations(this.reservations, draftRes)) {
        this.removeDraftReservation(draftRes.reservationId);
        if (displayMessage) this.showWarningMessage(this.$t('pageRoomplan.warningOverlapsWithOtherReservation', { roomNumber: draftRes.roomNumber }));
        return true;
      }

      if (this.overlapsWithSuppliedReservations(this.draftReservations, draftRes)) {
        this.removeDraftReservation(draftRes.reservationId);
        if (displayMessage) this.showWarningMessage(this.$t('pageRoomplan.warningOverlapsWithOtherReservation', { roomNumber: draftRes.roomNumber }));
        return true;
      }

      return false;
    },
    overlapsWithSuppliedReservations: function (reservations, draftRes, ignoreRoomNumber = false) {
      return reservations.filter(res => ignoreRoomNumber || res.roomNumber === draftRes.roomNumber)
        .filter(res => res.reservationId !== draftRes.reservationId)
        .filter((res) => {
          // eslint-disable-next-line radix
          if (parseInt(res.checkInDate) >= parseInt(draftRes.checkInDate) && parseInt(res.checkInDate) < parseInt(draftRes.checkOutDate)) return true;
          // eslint-disable-next-line radix
          if (parseInt(res.checkInDate) <= parseInt(draftRes.checkInDate) && parseInt(res.checkOutDate) > parseInt(draftRes.checkInDate)) return true;
          return false;
        }).length > 0;
    },
    removeDraftReservation: function (reservationId) {
      const res = this.draftReservations.find(it => it.reservationId === reservationId);
      if (!isNil(res)) {
        this.selectedRooms[res.roomNumber] = false;
      }
      this.draftReservations = this.draftReservations.filter(it => it.reservationId !== reservationId);
      if (isEmpty(this.draftReservations)) {
        this.cancelEdit();
      }
    },
    addDraftReservation: function (draftRes) {
      // in this case the spot is alrady taken

      this.draftReservations.forEach((it) => {
        it.autoEditMode = false;
      });
      draftRes.autoEditMode = true;
      this.$refs.roomplan.cancelEdit();
      this.draftReservations.push(draftRes);

      if (!isEmpty(this.selectableRooms)) {
        this.selectedRooms[draftRes.roomNumber] = true;
      }
    },
    updateSelectableRooms: function (draftRes) {
      if (isEmpty(this.draftReservations)) {
        this.cancelEdit();
      }

      const resByRoomNumber = values(groupBy(this.reservations, it => it.roomNumber));

      const overlappingRooms = [];

      resByRoomNumber.forEach((it) => {
        if (!isEmpty(it) && this.overlapsWithSuppliedReservations(it, draftRes, true)) {
          overlappingRooms.push(head(it).roomNumber);
        }
      });

      this.selectableRooms = this.roomNumbers.filter(it => !overlappingRooms.includes(it));
      this.selectedRooms = {};
      this.draftReservations.map(it => it.roomNumber).forEach((it) => { this.selectedRooms[it] = true; });
    },
    today: function () {
      this.startDate = this.$moment().add(-2, 'days').format('YYYYMMDD');
    },
    updateDate: function (offset) {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(offset, 'days').format('YYYYMMDD');
    },
    updateRoomplan: function () {
      const $vm = this;
      this.reservationUpdateInProgress = true;
      const promiseReservations = this.axios.get(this.serviceUrls.roomPlan(this), {
        params: {
          start: $vm.startDate,
          limit: $vm.numberOfDays,
          cacheBuster: Math.floor(new Date() / 1000),
        },
        headers: {
          Accept: 'application/x-protobuf',
        },
        responseType: 'arraybuffer',
      });

      const promiseAvailability = this.axios.get(this.serviceUrls.roomTypePlan(this), {
        params: {
          start: $vm.startDate,
          limit: $vm.numberOfDays,
          cacheBuster: Math.floor(new Date() / 1000),
        },
        headers: {
          Accept: 'application/x-protobuf',
        },
        responseType: 'arraybuffer',
      });

      this.updateInventory();

      this.reservations = [];
      this.roomTypeAvailability = null;

      Promise.all([promiseReservations, promiseAvailability])
        .then((arr) => {
          $vm.reservationUpdateInProgress = false;
          $vm.reservations = arr[0].data.data;
          $vm.roomTypeAvailability = arr[1].data.data;
          $vm.visibleDays = $vm.numberOfDays;
          $vm.visibleStartDate = $vm.startDate;

          /**
           * set roomNumber to null for all reservations which do have a non-existent room number assigned
           */
          $vm.reservations
            .filter(it => !$vm.roomNumbers.includes(it.roomNumber))
            .forEach((it) => { it.roomNumber = null; });

          /**
           * assign VRs (virtual room numbers) to all reservations without a room number assigned
           */
          $vm.virtualRoomCount = 0;

          $vm.reservations
            .filter(it => it.roomNumber == null)
            .forEach((it) => {
              $vm.assignVirtualRoomNumber(it);
            });

          $vm.lastUpdate = new Date().getTime();
        })
        .catch((error) => {
          $vm.reservationUpdateInProgress = false;
          this.showMessages(error.response);
          this.showErrorMessage('');
          this.$rollbar.error(error);
        });

      this.updatePaymentStatus();
      this.updateHousekeepingStatus();
    },
    updatePaymentStatus() {
      if (!this.hasRole('ROLE_USER')) return;
      if (isNil(this.startDate)) return;
      if (this.numberOfDays > 40) {
        this.paymentStatus = {};
      } else {
        this.axios.get(this.serviceUrls.roomPlanPaymentStatus(this), {
          params: {
            start: this.startDate,
            limit: this.numberOfDays,
          },
        }).then((response) => {
          this.paymentStatus = keyBy(response.data.data, 'reservationId');
        });
      }
    },
    updateHousekeepingStatus() {
      if (!this.hasRole('ROLE_USER')) return;
      this.axios.get(this.serviceUrls.roomPlanHousekeepingStatus(this)).then((response) => {
        this.housekeepingStatus = response.data.data.rooms;
      });
    },
    updateAvailability() {
      if (isNil(this.startDate)) return;
      const $vm = this;
      this.axios.get(this.serviceUrls.roomTypePlan(this), {
        params: {
          start: $vm.startDate,
          limit: $vm.numberOfDays,
          cacheBuster: Math.floor(new Date() / 1000),
        },
        headers: {
          Accept: 'application/x-protobuf',
        },
        responseType: 'arraybuffer',
      }).then((response) => {
        $vm.roomTypeAvailability = response.data.data;
      });

      this.updateHousekeepingStatus();
    },
    updateInventory() {
      if (!this.hasRole('ROLE_USER')) return;
      if (isNil(this.startDate)) return;
      this.axios.get(this.serviceUrls.inventory.find(this, this.startDate, this.numberOfDays)).then((response) => {
        this.inventory = Object.freeze(response.data.data);
      });
    },
    assignVirtualRoomNumber(reservation) {
      for (let i = 0; i < this.virtualRoomCount; i++) {
        if (this.canAssignRoomNumber(reservation, `VR::${i}`)) {
          reservation.roomNumber = `VR::${i}`;
          break;
        }
      }

      if (reservation.roomNumber === null) {
        this.virtualRoomCount++;
        reservation.roomNumber = `VR::${this.virtualRoomCount - 1}`;
      }
    },
    canAssignRoomNumber(reservation, roomNumber) {
      return this.reservations
        .filter(itInner => itInner.reservationId !== reservation.reservationId)
        .filter(itInner => itInner.roomNumber === roomNumber)
        .filter((itInner) => {
          if (itInner.checkInDate >= reservation.checkInDate && itInner.checkInDate < reservation.checkOutDate) {
            return true;
          }
          if (itInner.checkOutDate > reservation.checkInDate && itInner.checkOutDate <= reservation.checkOutDate) {
            return true;
          }
          if (itInner.checkInDate <= reservation.checkInDate && itInner.checkOutDate >= reservation.checkOutDate) {
            return true;
          }

          return false;
        })
        .length === 0;
    },
    setInventoryStatus(type) {
      const updates = this.draftReservations.map(it => ({
        roomNumber: it.roomNumber,
        startDate: it.checkInDate,
        endDate: this.$moment(it.checkOutDate, 'YYYYMMDD').add(-1, 'days').format('YYYYMMDD'),
        type: type,
        remark: null,
      }));

      if (isEmpty(updates)) return;

      let idx = 0;
      let errors = 0;

      const $vm = this;

      const handleResult = function (promise) {
        promise
          .then((response) => {
            if (errors === 0 && idx === updates.length) {
              $vm.showSuccessMessage();
            }
          })
          .catch((error) => {
            errors++;
            if (errors > 0 && idx === updates.length) {
              $vm.showErrorMessage();
            }
          })
          .finally(() => {
            if (idx === updates.length) {
              $vm.updateInventory();
              $vm.updateAvailability();
            } else {
              handleResult($vm.axios.post($vm.serviceUrls.inventory.update($vm), updates[idx++]));
            }
          });
      };

      handleResult(this.axios.post(this.serviceUrls.inventory.update(this), updates[idx++]));

      // Promise.all(updates.map(it => this.axios.post(this.serviceUrls.inventory.update(this), it)));


      this.cancelEdit();
    },
    loadHolidays() {
      try {
        this.axios.get(this.serviceUrls.holidays([this.hotelSettings.details.countryCode]))
          .then((response) => {
            this.holidays = response.data.data || null;
          });
      } catch (e) { /* ignore */ }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

  .z-index40 {
    z-index: 40;
  }
  .button-row {
    display: grid;
    justify-content: left;
    @media (min-width: $br-tablet){
      justify-content: end;
    }
    grid-template-columns: repeat(7, auto);
    grid-column-gap: 1.5rem;

    @media (min-width: $br-desktop){
      grid-template-columns: repeat(9, auto);
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/PageRoomplan.vue